import { categoryCost, contractCost } from './calculations.js';
import type { BudgetCalcOptions } from './calculations.js';
import type {
  CategoryBudget,
  CategoryId,
  CategorySplit,
  ContractDocument,
  Payment,
  SplitRow,
  Supplier,
} from './contract-document.js';
import {
  DEFAULT_PAYMENT_STATUS,
  DEFAULT_PAYMENT_UNIT,
  getCategory,
  paymentsOf,
} from './contract-document.js';
import type { SupplierRef } from './external-refs.js';

/**
 * Pure editing transforms over a contract document — the write-side companions
 * to the derived numbers in calculations.ts. Every function is
 * (document | category, input) → new value; nothing here touches storage.
 *
 * These encode the non-obvious write rules once, so the web editor, the
 * move-category endpoint and the tests all agree:
 *  - D11 (BR-12 retired): amount, factor and hours on Own Work are three
 *    independent user-owned values. Typing hours makes them sticky; nothing
 *    ever derives the factor. The cost per hour is the derived quantity.
 *  - BR-10: a moved category is frozen on the source and priced cost-first
 *    on the target.
 *  - CALC-17 (BR-19): split rows store share percentages; users type amounts,
 *    so amount edits rebase to sharePct at the display edge.
 */

/** Categories that can be billed via another contract (BR-10). */
export const MOVABLE_CATEGORY_IDS = ['exp', 'sub', 'mat'] as const;
export type MovableCategoryId = (typeof MOVABLE_CATEGORY_IDS)[number];

export const isMovableCategory = (id: CategoryId): id is MovableCategoryId =>
  (MOVABLE_CATEGORY_IDS as readonly CategoryId[]).includes(id);

const FACTOR_MIN = 0.1;
const FACTOR_MAX = 100;

const round2 = (value: number): number => Math.round(value * 100) / 100;
// Four decimals, not two: an uplift is entered by hand and shown as entered
// (2026-09-30), so `1.125` must survive the round trip. The schema only bounds it.
const round4 = (value: number): number => Math.round(value * 10_000) / 10_000;
const clampFactor = (value: number): number =>
  Math.min(FACTOR_MAX, Math.max(FACTOR_MIN, round4(value)));
const clampSharePct = (value: number): number => Math.min(100, Math.max(0, round2(value)));

/** Replace one category, immutably. */
export const updateCategory = (
  document: ContractDocument,
  id: CategoryId,
  update: (category: CategoryBudget) => CategoryBudget,
): ContractDocument => ({
  ...document,
  categories: document.categories.map((category) =>
    category.id === id ? update(category) : category,
  ),
});

// ─── Own Work: BR-12 hours ⇄ factor coupling ───

/**
 * Set Own Work hours (BR-12: hours become canonical). The factor derives so
 * that cost = hours × blendedCostRate still satisfies CALC-9:
 * factor = amount / (hours × rate). With no amount or no hours there is
 * nothing to derive from — the current factor stands.
 */
/**
 * Own Work hours as typed by the user. From now on they are the user's number
 * (sticky, D11): no later amount or factor edit recalculates them, and they
 * never derive the factor — the cost per hour absorbs the difference (CALC-23).
 */
export const setOwnHours = (document: ContractDocument, hours: number): ContractDocument =>
  updateCategory(document, 'own', (own) => ({
    ...own,
    ownHoursOverride: round2(Math.max(0, hours)),
  }));

/**
 * Set a category's target factor. A stored, user-owned number (D11): on Own
 * Work it leaves typed hours exactly where they are; recomputing the amount
 * instead would violate amount-canonicity (BR-8).
 */
export const setCategoryFactor = (
  document: ContractDocument,
  id: CategoryId,
  factor: number,
): ContractDocument =>
  updateCategory(document, id, (category) => ({ ...category, targetFactor: clampFactor(factor) }));

/**
 * CALC-27 — set the contract's target factor and derive the Own Work factor
 * that reaches it (2026-10-12): the cost the contract may carry at the target
 * is `revenue / target`; what the other distributed categories already cost
 * (CALC-9, under the same options the page calculates with) comes off it, and
 * the Own Work factor is `ownAmount / ownCostNeeded`.
 *
 *   ownFactor = ownAmount / (revenue / target − Σ cost(other categories))
 *
 * The target itself is stored whatever the outcome. When the formula gives
 * nothing usable — the other categories already cost more than the target
 * allows, no revenue, no Own Work amount — the Own Work factor stays as it
 * was: a negative or infinite factor is not a number anyone meant.
 */
export const setTargetFactor = (
  document: ContractDocument,
  target: number,
  options?: BudgetCalcOptions,
): ContractDocument => {
  const targetFactor = clampFactor(target);
  const own = getCategory(document, 'own');
  const otherCost = contractCost(document, options) - categoryCost(own, options);
  const ownCostNeeded = document.revenue / targetFactor - otherCost;
  const ownFactor = own.amount / ownCostNeeded;
  const next = { ...document, targetFactor };
  return ownCostNeeded > 0 && own.amount > 0 && Number.isFinite(ownFactor)
    ? setCategoryFactor(next, 'own', ownFactor)
    : next;
};

/**
 * Set a category amount (BR-8: amounts are canonical). Touches nothing else:
 * on Own Work the factor stays what the user set and typed hours stay typed
 * (D11) — the implied cost per hour moves instead.
 */
export const setCategoryAmount = (
  document: ContractDocument,
  id: CategoryId,
  amount: number,
): ContractDocument =>
  updateCategory(document, id, (category) => ({
    ...category,
    amount: Math.max(0, Math.round(amount)),
  }));

// ─── BR-10: billing a category via another contract ───

/**
 * Source side of a move: mark the category as billed via the sibling. The
 * amount stays (frozen) but the category leaves this contract's distribution
 * and cost (CALC-12/14 exclude `billedVia` lines).
 */
export const moveCategoryOut = (
  source: ContractDocument,
  categoryId: MovableCategoryId,
  targetContractId: string,
): ContractDocument =>
  updateCategory(source, categoryId, (category) => ({
    ...category,
    billedVia: targetContractId,
  }));

/** Source side of a move-back: the category rejoins this contract. */
export const moveCategoryBack = (
  source: ContractDocument,
  categoryId: MovableCategoryId,
): ContractDocument =>
  updateCategory(source, categoryId, ({ billedVia: _removed, ...category }) => category);

/**
 * The amount a move adds on the target: the source category's cost (CALC-9)
 * priced at the target's factor for the same category — the cost-first rule
 * (CALC-18 / CALC-14a) applied across contracts.
 */
export const movedAmountOnTarget = (
  sourceCategory: CategoryBudget,
  target: ContractDocument,
): number =>
  Math.round(categoryCost(sourceCategory) * getCategory(target, sourceCategory.id).targetFactor);

/**
 * Target side of a move: add the cost-first line and grow revenue (CALC-14a).
 * Additive — the target keeps any budget it already had in the category.
 */
export const receiveMovedCategory = (
  target: ContractDocument,
  sourceCategory: CategoryBudget,
): ContractDocument => {
  const delta = movedAmountOnTarget(sourceCategory, target);
  return updateCategory(
    { ...target, revenue: target.revenue + delta },
    sourceCategory.id,
    (category) => ({ ...category, amount: category.amount + delta, hidden: false }),
  );
};

/**
 * Target side of a move-back: subtract what the move added. Symmetric with
 * `receiveMovedCategory` as long as neither the source category nor the
 * target factor changed while moved (the UI freezes moved categories);
 * clamped at zero so a drifted factor can never produce a negative amount.
 */
export const releaseMovedCategory = (
  target: ContractDocument,
  sourceCategory: CategoryBudget,
): ContractDocument => {
  const delta = movedAmountOnTarget(sourceCategory, target);
  const current = getCategory(target, sourceCategory.id).amount;
  const removed = Math.min(current, delta);
  return updateCategory(
    { ...target, revenue: Math.max(0, target.revenue - removed) },
    sourceCategory.id,
    (category) => ({ ...category, amount: category.amount - removed }),
  );
};

// ─── BR-9: hiding categories ───

/** Hide a category (amount must already be 0 — callers zero it first, BR-9). */
export const setCategoryHidden = (
  document: ContractDocument,
  id: CategoryId,
  hidden: boolean,
): ContractDocument =>
  updateCategory(document, id, (category) => ({
    ...category,
    hidden,
    ...(hidden ? { amount: 0 } : {}),
  }));

// ─── CALC-17 split editing (BR-19: informational shares) ───

const emptySplit = (id: CategoryId): CategorySplit =>
  id === 'sub' ? { kind: 'suppliers', suppliers: [], unassigned: [] } : { kind: 'rows', rows: [] };

const withSplit = (
  document: ContractDocument,
  id: CategoryId,
  update: (split: CategorySplit) => CategorySplit,
): ContractDocument =>
  updateCategory(document, id, (category) => ({
    ...category,
    split: update(category.split ?? emptySplit(id)),
  }));

/** Append a row: `rows` for exp/mat, the unassigned pool for sub. */
export const addSplitRow = (
  document: ContractDocument,
  id: CategoryId,
  row: { id: string; name: string; sharePct?: number },
): ContractDocument =>
  withSplit(document, id, (split) => {
    const entry: SplitRow = { id: row.id, name: row.name, sharePct: round2(row.sharePct ?? 0) };
    return split.kind === 'rows'
      ? { ...split, rows: [...split.rows, entry] }
      : { ...split, unassigned: [...split.unassigned, entry] };
  });

/** Remove a row wherever it lives: rows, unassigned, a supplier, or its types. */
export const removeSplitRow = (
  document: ContractDocument,
  id: CategoryId,
  rowId: string,
): ContractDocument =>
  withSplit(document, id, (split) => {
    if (split.kind === 'rows') {
      return { ...split, rows: split.rows.filter((row) => row.id !== rowId) };
    }
    return {
      ...split,
      unassigned: split.unassigned.filter((row) => row.id !== rowId),
      suppliers: split.suppliers
        .filter((supplier) => supplier.id !== rowId)
        .map((supplier) => ({
          ...supplier,
          types: supplier.types.filter((type) => type.id !== rowId),
        })),
    };
  });

/** Rename a row/supplier/type in place. */
export const renameSplitRow = (
  document: ContractDocument,
  id: CategoryId,
  rowId: string,
  name: string,
): ContractDocument =>
  withSplit(document, id, (split) => {
    const rename = <T extends { id: string; name: string }>(entries: T[]): T[] =>
      entries.map((entry) => (entry.id === rowId ? { ...entry, name } : entry));
    if (split.kind === 'rows') return { ...split, rows: rename(split.rows) };
    return {
      ...split,
      unassigned: rename(split.unassigned),
      suppliers: rename(split.suppliers).map((supplier) => ({
        ...supplier,
        types: rename(supplier.types),
      })),
    };
  });

/**
 * The user types an amount; the store keeps a share (BR-8 analogue for splits).
 * Top-level entries (rows, unassigned, suppliers) rebase against the category
 * amount; a supplier's types rebase against that supplier's amount (CALC-17).
 */
export const setSplitRowAmount = (
  document: ContractDocument,
  id: CategoryId,
  rowId: string,
  amount: number,
): ContractDocument => {
  const parentAmount = getCategory(document, id).amount;
  const pctOf = (base: number): number => (base > 0 ? clampSharePct((amount / base) * 100) : 0);

  return withSplit(document, id, (split) => {
    if (split.kind === 'rows') {
      return {
        ...split,
        rows: split.rows.map((row) =>
          row.id === rowId ? { ...row, sharePct: pctOf(parentAmount) } : row,
        ),
      };
    }
    return {
      ...split,
      unassigned: split.unassigned.map((row) =>
        row.id === rowId ? { ...row, sharePct: pctOf(parentAmount) } : row,
      ),
      suppliers: split.suppliers.map((supplier) => {
        if (supplier.id === rowId) return { ...supplier, sharePct: pctOf(parentAmount) };
        const supplierAmount = (parentAmount * supplier.sharePct) / 100;
        return {
          ...supplier,
          types: supplier.types.map((type) =>
            type.id === rowId ? { ...type, sharePct: pctOf(supplierAmount) } : type,
          ),
        };
      }),
    };
  });
};

/** Add a supplier to the sub-contractors split. */
export const addSupplier = (
  document: ContractDocument,
  supplier: { id: string; name: string; sharePct?: number; supplierRef?: SupplierRef },
): ContractDocument =>
  withSplit(document, 'sub', (split) => {
    if (split.kind !== 'suppliers') return split;
    const entry: Supplier = {
      id: supplier.id,
      name: supplier.name,
      // Set when picked from the register (v8); blank lines carry no ref.
      ...(supplier.supplierRef === undefined ? {} : { supplierRef: supplier.supplierRef }),
      sharePct: round2(supplier.sharePct ?? 0),
      types: [],
    };
    return { ...split, suppliers: [...split.suppliers, entry] };
  });

/** Add a type row under one supplier. */
export const addSupplierType = (
  document: ContractDocument,
  supplierId: string,
  row: { id: string; name: string; sharePct?: number },
): ContractDocument =>
  withSplit(document, 'sub', (split) => {
    if (split.kind !== 'suppliers') return split;
    return {
      ...split,
      suppliers: split.suppliers.map((supplier) =>
        supplier.id === supplierId
          ? {
              ...supplier,
              types: [
                ...supplier.types,
                { id: row.id, name: row.name, sharePct: round2(row.sharePct ?? 0) },
              ],
            }
          : supplier,
      ),
    };
  });

/**
 * Move an unassigned row under a supplier, rebasing its share: as a pool row
 * the share is % of the category; as a type it is % of the supplier —
 * same absolute amount either way (CALC-17).
 */
export const assignRowToSupplier = (
  document: ContractDocument,
  rowId: string,
  supplierId: string,
): ContractDocument =>
  withSplit(document, 'sub', (split) => {
    if (split.kind !== 'suppliers') return split;
    const row = split.unassigned.find((candidate) => candidate.id === rowId);
    const supplier = split.suppliers.find((candidate) => candidate.id === supplierId);
    if (!row || !supplier) return split;

    const rebased: SplitRow = {
      ...row,
      sharePct: supplier.sharePct > 0 ? clampSharePct((row.sharePct / supplier.sharePct) * 100) : 0,
    };
    return {
      ...split,
      unassigned: split.unassigned.filter((candidate) => candidate.id !== rowId),
      suppliers: split.suppliers.map((candidate) =>
        candidate.id === supplierId
          ? { ...candidate, types: [...candidate.types, rebased] }
          : candidate,
      ),
    };
  });

/**
 * Removes one rate-card entry. If it was the T&M default hourly rate, the
 * pointer goes with it — the document must never name an entry it lacks.
 */
export const removeRateCardEntry = (
  document: ContractDocument,
  entryId: string,
): ContractDocument => {
  const rateCard = document.rateCard.filter((entry) => entry.id !== entryId);
  if (rateCard.length === document.rateCard.length) return document;
  if (document.tmDefaultRateEntryId !== entryId) return { ...document, rateCard };
  const rest = { ...document, rateCard };
  delete rest.tmDefaultRateEntryId;
  return rest;
};

/** Clears the whole rate card and the default hourly rate with it (a move to fixed price, BR-11). */
export const clearRateCard = (document: ContractDocument): ContractDocument => {
  if (document.rateCard.length === 0 && document.tmDefaultRateEntryId === undefined)
    return document;
  const rest = { ...document, rateCard: [] };
  delete rest.tmDefaultRateEntryId;
  return rest;
};

/** Picks (or, with undefined, clears) the T&M default hourly rate; the id must be on the card. */
export const setTmDefaultRate = (
  document: ContractDocument,
  entryId: string | undefined,
): ContractDocument => {
  if (entryId === undefined) {
    if (document.tmDefaultRateEntryId === undefined) return document;
    const rest = { ...document };
    delete rest.tmDefaultRateEntryId;
    return rest;
  }
  if (!document.rateCard.some((entry) => entry.id === entryId)) return document;
  return document.tmDefaultRateEntryId === entryId
    ? document
    : { ...document, tmDefaultRateEntryId: entryId };
};

// ─── Payment schedule (BR-31, CALC-26) ───

/**
 * The amount a payment's own figures suggest (CALC-26):
 * `quantity × rate × (1 − discountPct / 100)`, rounded to whole currency units.
 * A helper only — the stored amount is the PM's number (BR-31), so this is what
 * an amount is PREFILLED with when the line's figures change, never a
 * recalculation of an amount already typed.
 */
export const paymentAmountSuggested = (
  payment: Pick<Payment, 'quantity' | 'rate' | 'discountPct'>,
): number => Math.round(payment.quantity * payment.rate * (1 - payment.discountPct / 100));

/** Σ amount over the schedule — what the customer is scheduled to be invoiced. */
export const paymentScheduleTotal = (document: ContractDocument): number =>
  paymentsOf(document).reduce((sum, payment) => sum + payment.amount, 0);

/**
 * Append a payment. Everything not given starts at its neutral value (one
 * piece, no rate, no discount, no amount) so a fresh line asks for nothing but
 * the date it already has; the id is minted here so the caller cannot duplicate one.
 */
export const addPayment = (
  document: ContractDocument,
  payment: Partial<Omit<Payment, 'id'>> & Pick<Payment, 'invoiceDate'>,
  id: string = globalThis.crypto.randomUUID(),
): ContractDocument => ({
  ...document,
  payments: [
    ...paymentsOf(document),
    {
      id,
      invoiceDate: payment.invoiceDate,
      ...(payment.invoiceText === undefined ? {} : { invoiceText: payment.invoiceText }),
      quantity: payment.quantity ?? 1,
      unit: payment.unit ?? DEFAULT_PAYMENT_UNIT,
      rate: payment.rate ?? 0,
      discountPct: payment.discountPct ?? 0,
      amount: payment.amount ?? 0,
      status: payment.status ?? DEFAULT_PAYMENT_STATUS,
    },
  ],
});

/**
 * Change one payment's figures. When quantity, rate or discount move and
 * `amount` is not part of the change, the amount is prefilled from CALC-26 —
 * unless the PM has typed an amount the figures did not already suggest, in
 * which case it is theirs and stays (BR-31: typed, sticky). An unknown id is a
 * no-op that returns the same document.
 */
export const updatePayment = (
  document: ContractDocument,
  id: string,
  change: Partial<Omit<Payment, 'id'>>,
): ContractDocument => {
  const payments = paymentsOf(document);
  const index = payments.findIndex((payment) => payment.id === id);
  if (index < 0) return document;
  const current = payments[index]!;
  const next: Payment = { ...current, ...change };
  if (next.invoiceText !== undefined && next.invoiceText.trim() === '') delete next.invoiceText;
  const figuresMoved =
    next.quantity !== current.quantity ||
    next.rate !== current.rate ||
    next.discountPct !== current.discountPct;
  if (figuresMoved && change.amount === undefined) {
    const amountWasSuggested = current.amount === paymentAmountSuggested(current);
    if (amountWasSuggested) next.amount = paymentAmountSuggested(next);
  }
  const updated = payments.slice();
  updated[index] = next;
  return { ...document, payments: updated };
};

/** Remove a payment; removing the last one leaves the document without the key, as it began. */
export const removePayment = (document: ContractDocument, id: string): ContractDocument => {
  const remaining = paymentsOf(document).filter((payment) => payment.id !== id);
  const rest: ContractDocument = { ...document };
  delete rest.payments;
  return remaining.length === 0 ? rest : { ...rest, payments: remaining };
};
